// DLC_03 / ARON — types. Mirrors the backend serializers + the Centrifugo stream
// event shapes published by apps.aron.services.publisher.

export type AronReasoningMode =
  | "express"
  | "fast"
  | "standard"
  | "deep"
  | "deepseek"
  | "deepseek_pro"
  | "gpt5_mini"
  | "gpt5_nano"
  | "luna"
  | "astra";
export type AronAccessLevel = "read_only" | "write_no_delete";

export interface AronConversation {
  id: string;
  title: string;
  model_id: string;
  reasoning_mode: AronReasoningMode;
  access_level: AronAccessLevel;
  autopilot?: boolean;
  /** „Wyłącz limit kroków" — per-turn iteration/bridge caps lifted (YOLO-permission holders). */
  unlimited_steps?: boolean;
  mission_success?: boolean | null;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: string;
  channel: string;
  /** True while a turn is in flight — drives the list spinner. */
  is_generating?: boolean;
  /** Write proposals still awaiting my approve/reject click in this thread. */
  pending_writes_count?: number;
  /** Zadania w tle, na które rozmowa CZEKA (ARON „zlecił, wróci"). Pusta lista =
   *  nikt sam nie wróci — i ekran ma to pokazać, zamiast milczeć. */
  background_waits?: AronBackgroundWaitInfo[];
}

/** Jeden znacznik powrotu: co zlecono, od kiedy wisi i do kiedy system obiecuje
 *  wrócić nawet bez wyniku (deadline). */
export interface AronBackgroundWaitInfo {
  task_id: string;
  label: string;
  since: string;
  deadline: string | null;
}

/** A thread holding write proposals that still wait for my approve/reject click.
 * Returned by /conversations/pending-writes/ — the exact set behind the sidebar
 * badge, unpaginated, so every counted proposal can be reached from the UI. */
export interface AronPendingWriteThread {
  id: string;
  title: string;
  persona: string;
  pending_writes_count: number;
  updated_at: string;
  /** "mail" = thread born from an e-mail to ARON (counts under Poczta ARON). */
  source: "chat" | "mail";
}

export type AronMessageRole = "user" | "assistant";
export type AronMessageStatus =
  | "pending"
  | "streaming"
  | "done"
  | "error"
  | "cancelled";

export interface AronToolStep {
  id: string;
  kind: string; // "api_query" | "code" | "web_search"
  /** Plain-Polish, non-technical description of what this call does. */
  label: string;
  status: "running" | "done" | "error";
  /** Full request data, shown in an expandable detail row. */
  request?: {
    method: string;
    path: string;
    query?: Record<string, unknown>;
    status_code?: number | null;
  };
  detail?: string; // legacy
}

export interface AronSource {
  entity_type: string;
  entity_id: string;
  path: string;
}

export interface AronFileResult {
  id: string;
  filename: string;
  content_type: string;
  url: string; // backend proxy download URL
}

// A file the USER attached to their message (image / PDF).
export interface AronAttachment {
  id: string;
  filename: string;
  content_type: string;
  url: string; // S3 (presigned) / media URL, or a local object URL while optimistic
}

// Faza 3: a write ARON proposed; executed only after the user approves it.
export type AronPendingWriteStatus =
  | "pending"
  | "approved"
  | "executed"
  | "failed"
  | "rejected"
  // ARON cancelled its own still-pending card via withdraw_proposals, on the
  // user's explicit request — terminal like "rejected", nothing was executed.
  | "withdrawn";

/** A dependent step proposed together with the main write; `{{created_id}}` in
 * path/body is replaced server-side with the id of the created object. */
export interface AronWriteFollowUp {
  method: string;
  path: string;
  body?: Record<string, unknown>;
  description: string;
}

export type AronPendingWriteKind = "api_write" | "schedule_task";

/** Plik dopinany do zapisu. `origin` mówi, CZYJ jest: "user" = przysłał go
 * człowiek w tej rozmowie, "aron" = ARON wytworzył go w maszynie i wydał
 * `deliver`em, "unknown" = pliku już nie ma. Zatwierdzający musi to widzieć,
 * zanim wpuści do systemu wytwór maszyny. */
export interface AronWriteAttachment {
  field: string;
  attachment_id: string;
  filename: string;
  content_type: string;
  origin?: "user" | "aron" | "unknown";
}

export interface AronPendingWrite {
  id: string;
  method: string;
  path: string;
  body: Record<string, unknown>;
  description: string;
  follow_ups?: AronWriteFollowUp[];
  attachments?: AronWriteAttachment[];
  status: AronPendingWriteStatus;
  kind?: AronPendingWriteKind;
  auto_executed?: boolean;
  scheduled_task?: string | null;
  response_status: number | null;
  response_summary: Record<string, unknown> | null;
  resolved_at: string | null;
  created_at: string;
}

// ── Zadania cykliczne (scheduled tasks) ─────────────────────────────────────

export type AronScheduledTaskVisibility = "personal" | "shared";
/** How much a scheduled run may DO — the same three levels as an ARON chat. */
export type AronScheduledTaskRunMode = "read_only" | "proposals" | "autopilot";
export type AronScheduledTaskApproval = "approved" | "pending_ceo" | "rejected";
export type AronScheduledTaskRunStatus =
  | "running"
  | "ok"
  | "error"
  | "skipped_budget"
  | "report_failed";

export interface AronWriteScopeEntry {
  method: string;
  path_template: string;
  allowed_fields?: string[];
}

/** One armed operation as the server describes it in plain Polish (authorization panel). */
export interface AronWriteScopeDescribed extends AronWriteScopeEntry {
  allowed_fields: string[];
  description: string;
}

export interface AronAudienceSelector {
  type: "ceo" | "technical_directors" | "user";
  id?: string;
}

export interface AronScheduledTaskLastRun {
  id: string;
  status: AronScheduledTaskRunStatus;
  status_display: string;
  finished_at: string | null;
  conversation: string | null;
  cost_usd: string;
}

export interface AronScheduledTaskModeInfo {
  label: string;
  /** Concrete model name, e.g. "Haiku 4.5" ("" when the mode is flag-disabled). */
  model: string;
  /** Preset detail, e.g. "bez myślenia" / "myślenie · effort medium". */
  detail: string;
  cost: string;
}

export interface AronScheduledTask {
  id: string;
  name: string;
  instruction: string;
  cron_expression: string;
  cron_human: string;
  timezone: string;
  /** Next few fire times (ISO). Empty when the task has no future: paused, rejected, awaiting CEO. */
  next_runs: string[];
  reasoning_mode: AronReasoningMode;
  reasoning_mode_display: string;
  mode_info: AronScheduledTaskModeInfo;
  run_mode: AronScheduledTaskRunMode;
  run_mode_display: string;
  /** Empty = the report is delivered only on the planner page. */
  email_to: string[];
  email_cc: string[];
  /** Finished runs nobody has opened yet — drives the "new result" badge. */
  unread_runs: number;
  write_scope: AronWriteScopeEntry[];
  write_scope_human: AronWriteScopeDescribed[];
  max_writes_per_run: number;
  visibility: AronScheduledTaskVisibility;
  shared_audience: AronAudienceSelector[];
  audience_human: string;
  approval_status: AronScheduledTaskApproval;
  approval_status_display: string;
  rejection_reason: string;
  is_active: boolean;
  paused_reason: string;
  next_run_at: string | null;
  last_run_at: string | null;
  consecutive_failures: number;
  owner: string;
  owner_name: string;
  is_owner: boolean;
  can_manage: boolean;
  last_run: AronScheduledTaskLastRun | null;
  created_at: string;
}

/** One fire time on the planner axis, from GET scheduled-tasks/upcoming/. */
export interface AronPlannerOccurrence {
  task: string;
  run_at: string;
}

export interface AronPlannerTimelineMeta {
  days: number;
  from: string;
  to: string;
  count: number;
  /** Tasks whose occurrence list hit the per-task cap — the axis is short, not complete. */
  truncated_tasks: string[];
  truncated_total: boolean;
}

export interface AronPlannerTimeline {
  occurrences: AronPlannerOccurrence[];
  meta: AronPlannerTimelineMeta;
}

export interface AronPlannerOptions {
  /** False = the dispatcher is off; tasks can be defined but nothing will fire. */
  scheduled_tasks_enabled: boolean;
  min_interval_minutes: number;
  max_writes_per_run: number;
  write_enabled: boolean;
  timezone: string;
  can_share: boolean;
  /** Whether this user may arm a task that writes unattended (CEO or the flag). */
  can_autopilot: boolean;
  run_modes: { value: AronScheduledTaskRunMode; label: string; detail: string }[];
  audience_options: { type: string; label: string }[];
}

export interface AronSchedulePreview {
  valid: boolean;
  error: string;
  human: string;
  next_runs: string[];
}

export interface AronScopeValidation {
  valid: boolean;
  error: string;
  entries: AronWriteScopeDescribed[];
}

/** The definition body the planner form sends (create = all of it, edit = a subset). */
export interface AronScheduledTaskInput {
  name?: string;
  instruction?: string;
  cron_expression?: string;
  reasoning_mode?: AronReasoningMode;
  run_mode?: AronScheduledTaskRunMode;
  email_to?: string[];
  email_cc?: string[];
  write_scope?: AronWriteScopeEntry[];
  max_writes_per_run?: number;
  visibility?: AronScheduledTaskVisibility;
  shared_audience?: AronAudienceSelector[];
  is_active?: boolean;
  /** Required by the server whenever a non-empty write scope is being armed. */
  confirm_write_scope?: boolean;
}

export interface AronScheduledTaskRun {
  id: string;
  status: AronScheduledTaskRunStatus;
  status_display: string;
  manual: boolean;
  error: string;
  started_at: string | null;
  finished_at: string | null;
  input_tokens: number;
  output_tokens: number;
  cost_usd: string;
  auto_writes_count: number;
  pending_writes_count: number;
  report_sent_at: string | null;
  report_recipients: string[];
  read_at: string | null;
  report_text: string;
  conversation: string | null;
  created_at: string;
}

/** Per-step outcome of an executed follow-up sequence (response_summary.steps). */
export interface AronWriteStepResult {
  index: number;
  method: string;
  path: string;
  description: string;
  status: number | null;
  ok: boolean;
  skipped: boolean;
  detail: string;
  entity_type: string;
  entity_id: string;
}

export function writeStepResults(write: AronPendingWrite): AronWriteStepResult[] {
  const steps = write.response_summary?.steps;
  return Array.isArray(steps) ? (steps as AronWriteStepResult[]) : [];
}

/** The main write went through but a dependent step did not — the object is
 *  half-finished, so the card must NOT read as a plain success. */
export function writeStepsError(write: AronPendingWrite): string {
  const err = write.response_summary?.steps_error;
  return write.status === "executed" && typeof err === "string" ? err : "";
}

/** entity_type/entity_id of the object the main write touched/created. */
export function writeEntity(
  write: AronPendingWrite,
): { type: string; id: string } | null {
  const rs = write.response_summary;
  const type = typeof rs?.entity_type === "string" ? rs.entity_type : "";
  const id = typeof rs?.entity_id === "string" ? rs.entity_id : "";
  return type && id ? { type, id } : null;
}

/** Login + hasło, które API pokazało JEDEN RAZ przy wykonaniu zapisu (konto
 * podwykonawcy: utworzenie albo reset hasła). Backend NIE zapisuje ich w bazie —
 * są tylko w odpowiedzi na zatwierdzenie, więc po odświeżeniu rozmowy znikają
 * dokładnie tak, jak na karcie podwykonawcy. Zgubione = do wylosowania nowego. */
export interface AronWriteCredentials {
  username?: string;
  password: string;
}

export function writeCredentials(
  write: AronPendingWrite,
): AronWriteCredentials | null {
  const creds = write.response_summary?.credentials;
  if (!creds || typeof creds !== "object") return null;
  const { username, password } = creds as Record<string, unknown>;
  if (typeof password !== "string" || !password) return null;
  return {
    password,
    ...(typeof username === "string" && username ? { username } : {}),
  };
}

/** Outcome of an optional follow-up action chained onto an executed write
 * (response_summary.chain — e.g. approve an invoice draft right after create). */
export interface AronWriteChainResult {
  action: string;
  path: string;
  status: number | null;
  ok: boolean;
  detail: string;
}

export function writeChainResult(
  write: AronPendingWrite,
): AronWriteChainResult | null {
  const chain = write.response_summary?.chain;
  return chain && typeof chain === "object"
    ? (chain as AronWriteChainResult)
    : null;
}

export interface AronMessage {
  id: string;
  role: AronMessageRole;
  text: string;
  /** Model reasoning — streamed live during the turn and PERSISTED (served by the
   * REST API + message.end event), so it stays in a collapsible block afterwards.
   * Empty for providers without thinking. */
  thinking?: string;
  status: AronMessageStatus;
  /** Ocena niezależnego kontrolera jakości — obecna dopiero po zakończeniu tury. */
  gate?: AronQualityGate;
  /** Tekst z tury, która jeszcze trwa (odtworzony po odświeżeniu strony). */
  partial?: boolean;
  stop_reason?: string;
  error?: string | null;
  /** Model, który wykonał TĘ turę. Model rozmowy da się przełączyć w trakcie, więc
   *  wątek potrafi mieć kilka modeli — puste na wiadomościach użytkownika i na
   *  turach sprzed wprowadzenia znacznika. */
  model_id?: string;
  /** Nazwa modelu tej tury do pokazania („Opus 5") — z backendu, żeby UI nie nazywał
   *  modelu inaczej niż rozliczenie. */
  model_label?: string;
  cost_usd?: string;
  /** Ile zapytań do dostawców poszło w TEJ turze (kroki pętli + pliki czytane po
   *  kolei) — z żywego zdarzenia `usage`, więc tylko w trakcie tury. */
  provider_calls?: number;
  created_at: string;
  tool_steps: AronToolStep[];
  sources: AronSource[];
  files?: AronFileResult[];
  attachments?: AronAttachment[];
  pending_writes?: AronPendingWrite[];
  /** Dopisek w trakcie odpowiedzi: id wiersza asystenta, do którego tury został
   *  dołożony. W numeracji stoi ZA tą odpowiedzią — UI podpisuje go, żeby czytelnik
   *  wiedział, że wpłynął na odpowiedź wyżej, a nie zaczął nowej. */
  addendum_to?: string | null;
  /** Kiedy pętla tury naprawdę go przeczytała; puste = tura skończyła się wcześniej
   *  i dopisek dostał własną odpowiedź poniżej. */
  addendum_seen_at?: string | null;
}

/** Limity załączników trybu = limity DOSTAWCY tego trybu (backend:
 *  services/modes.py → attachment_limits). Bajty SUROWE (przed base64). */
export interface AronAttachmentLimits {
  provider: string;
  provider_label: string;
  vision: boolean;
  max_files: number;
  /** PDF — jeden plik. 0 = model bez wzroku. */
  file_max_bytes: number;
  /** Obraz — jeden plik (u Anthropica niższy niż PDF). */
  image_max_bytes: number;
  /** Obrazy + PDF-y RAZEM w jednym zapytaniu (historia rozmowy jedzie od nowa). */
  request_max_bytes: number;
  /** Strony PDF łącznie na zapytanie; 0 = bez limitu / nie dotyczy. */
  pdf_max_pages: number;
  /** DXF/DOCX/XLSX/TXT — jadą jako tekst, poza budżetem bajtów. */
  text_file_max_bytes: number;
  /** Skąd te liczby — do dymka. */
  source: string;
}

export interface AronWorkMode {
  value: AronReasoningMode;
  label: string;
  model: string;
  /** Cost tier glyph shown next to the label (¢ / $ / $$ / $$$). */
  cost: string;
  /** Short preset detail, e.g. "myślenie · effort medium". */
  detail: string;
  hint: string;
  /** Whether this model accepts image/PDF input — gates the attach + paste UI. */
  vision: boolean;
  /** Czy model przyjmie dopisek W TRAKCIE odpowiedzi (blok tekstu po wynikach
   *  narzędzi) — bramka pola „dopisz" obok Stop. Backend jest źródłem prawdy. */
  addendum: boolean;
  /** Limity załączników tego trybu — wypisywane przy modelu i egzekwowane w
   *  kompozytorze. Opcjonalne tylko dla starszych odpowiedzi/mocków. */
  limits?: AronAttachmentLimits;
}

// Faza 4 / 11E — CEO „Wydatki ARON" (spend monitoring + provider split + budgets).
export type AronProvider = "claude" | "deepseek";

export interface AronCostModel {
  model_id: string;
  provider: AronProvider;
  conversations: number;
  cost_usd: string;
}

export interface AronCostRow {
  user_id: string;
  user_name: string;
  email: string;
  conversation_count: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  total_tokens: number;
  cost_usd: string;
  cost_claude_usd: string;
  cost_deepseek_usd: string;
  models: AronCostModel[];
  tokens_today: number;
  /** Ile ten człowiek wydał DZIŚ (USD) — to jest miara limitu, nie tokeny. */
  cost_today_usd: string;
  /** Effective daily cap for this user in USD (per-user override → global default). */
  daily_cost_budget_usd: string;
  has_budget_override: boolean;
  last_activity_at: string | null;
}

export interface AronCostsMeta {
  count: number;
  total_cost_usd: string;
  total_cost_claude_usd: string;
  total_cost_deepseek_usd: string;
  total_tokens: number;
  daily_cost_budget_usd: string;
  conversation_cost_budget_usd: string;
}

// 11C / 11C-EXT — ARON knowledge base (CEO-managed notes + ARON suggestions).
export type AronKnowledgeSource = "seed" | "user" | "aron" | "code" | "meta";
export type AronKnowledgeStatus = "active" | "pending" | "rejected";

export interface AronKnowledgeNote {
  id: string;
  title: string;
  short: string;
  full: string;
  tags: string[];
  source: AronKnowledgeSource;
  source_display: string;
  status: AronKnowledgeStatus;
  status_display: string;
  seed_key: string;
  /** Why ARON proposed it (provenance for suggestions). */
  why: string;
  suggested_in: string | null;
  suggested_in_title: string;
  created_by: string | null;
  created_by_name: string;
  approved_by: string | null;
  approved_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface AronKnowledgeNoteInput {
  title: string;
  short: string;
  full: string;
  tags: string[];
}

// META-ARON faza 3 — offline analyzer runs + findings dashboard.
export type MetaRunStatus = "ok" | "error" | "skipped";
export type MetaFindingKind = "knowledge" | "feature" | "bug" | "prompt_rule";
export type MetaFindingStatus = "proposed" | "routed" | "duplicate" | "dismissed";

export interface MetaAronRun {
  id: string;
  created_at: string;
  window_start: string;
  window_end: string;
  model_id: string;
  status: MetaRunStatus;
  status_display: string;
  conversations_analyzed: number;
  signals_count: number;
  findings_count: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: string;
  error: string;
}

export interface MetaAronFinding {
  id: string;
  run: string;
  run_created_at: string;
  kind: MetaFindingKind;
  kind_display: string;
  title: string;
  summary: string;
  detail: string;
  why: string;
  confidence: string;
  status: MetaFindingStatus;
  status_display: string;
  knowledge_note: string | null;
  knowledge_note_status: string;
  dev_issue: string | null;
  dev_issue_title: string;
  evidence_conversation_ids: string[];
  created_at: string;
}

// 11E/C — CEO+DEV cross-user conversation audit ("Audyt ARON").
export interface AronAdminConversation {
  id: string;
  user: string;
  user_name: string;
  user_email: string;
  title: string;
  model_id: string;
  provider: AronProvider;
  reasoning_mode: AronReasoningMode;
  access_level: AronAccessLevel;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: string;
  message_count: number;
  created_at: string;
  last_message_at: string | null;
  /** Ostatnia aktywność w wątku (najnowsza wiadomość) — po tym lista sortuje i to pokazuje. */
  last_activity_at: string | null;
  deleted_at: string | null;
}

export interface AronAdminConversationDetail extends AronAdminConversation {
  messages: AronMessage[];
}

// One employee in the audit's people filter. Comes from the whole table (endpoint
// admin/conversations/owners/), not from the page currently on screen.
export interface AronAdminOwner {
  id: string;
  name: string;
  email: string;
  conversation_count: number;
}

// ── Stream events on channel aron:{conversation_id} ──────────────────────────
export interface AronQualityGate {
  role: "kontroler";
  phase?: "start";
  status?: string;
  passed?: boolean;
  score?: number | null;
  threshold?: number | null;
  reason?: string;
  attempt?: number;
  max_attempts?: number;
  rejected_text?: string;
}

export type AronStreamEvent =
  | { type: "message.start"; message_id: string }
  | { type: "delta"; message_id: string; text: string }
  | { type: "thinking"; message_id: string; text: string }
  | { type: "tool_step"; message_id: string; step: AronToolStep }
  | { type: "file"; message_id: string; file: AronFileResult }
  | { type: "write_proposal"; message_id: string; write: AronPendingWrite }
  | { type: "write_resolved"; message_id: string; write: AronPendingWrite }
  | {
      // Niezależny KONTROLER JAKOŚCI ocenia odpowiedź po jej zakończeniu. Zdarzenie
      // leci dwa razy: z phase="start" (żeby użytkownik wiedział, na co czeka)
      // i z wynikiem. Domyślnie nic nie cofa — decyzję o poprawce podejmuje człowiek.
      type: "quality_gate";
      message_id: string;
      gate: AronQualityGate;
    }
  | {
      type: "message.end";
      message_id: string;
      message: {
        id: string;
        role: "assistant";
        content: string;
        thinking?: string;
        sources: AronSource[];
        status: AronMessageStatus;
        /** Model, który wykonał TĘ turę (rozmowa może go zmienić dalej). */
        model_id?: string;
        cost_usd: string;
      };
    }
  | { type: "error"; message_id: string; error: string }
  | { type: "cancelled"; message_id: string }
  /** Koszt tury DOTĄD — po każdym zapytaniu do dostawcy (także za pliki czytane po
   *  kolei w analyze_files), żeby Stop dało się nacisnąć, zanim rachunek urośnie. */
  | {
      type: "usage";
      message_id: string;
      cost_usd: string;
      provider_calls: number;
      input_tokens: number;
      output_tokens: number;
    }
  /** Dopisek użytkownika przyjęty do TRWAJĄCEJ tury (z tej albo innej karty) —
   *  pełny wiersz, dokładany do transkryptu, jeśli go tam jeszcze nie ma. */
  | { type: "user_addendum"; message_id: string; message: AronMessage }
  /** Pętla tury przeczytała dopisek — od tego kroku model o nim wie. */
  | { type: "addendum_seen"; message_id: string; addendum_id: string }
  /** Tura wycofana — baza wróciła do stanu sprzed tury. Zdarzenie KOŃCZY turę:
   *  bez niego pole wysyłki zostawało zablokowane aż do odświeżenia strony. */
  | { type: "turn_rolled_back"; message_id: string; reason: string }
  /** A finished assistant note posted outside a turn (e.g. a follow-up sequence
   *  that broke after approval) — appended to the transcript as-is. */
  | {
      type: "message_appended";
      message: { id: string; text: string; created_at: string };
    };

export interface SendMessageResponse {
  conversation_id: string;
  channel: string;
  /** Null, gdy wiadomość weszła jako dopisek do trwającej tury (nie ma nowego zadania). */
  task_id: string | null;
  user_message: AronMessage;
  assistant_message_id: string;
  /** True = dopisek do TRWAJĄCEJ odpowiedzi; `assistant_message_id` wskazuje ją. */
  addendum?: boolean;
}
